import styled, { css } from "styled-components";
import { motion } from "framer-motion";
import { Logo, palette, Card, pxToRem, modularScale } from "@ottatech/design";
import { Loading } from "@ottatech/shared-components";
import { lazy, Suspense } from "react";

import { ApplicationProcess } from "./components/ApplicationProcess";
import { CompanyAdditionalInfo } from "./components/CompanyAdditionalInfo";
import { CompanyArticles } from "./components/CompanyArticles";
import { CompanyBenefits } from "./components/CompanyBenefits";
import { CompanyLogo } from "./components/CompanyLogo";
import { CompanyMission } from "./components/CompanyMission";
import { CompanyPhotos } from "./components/CompanyPhotos";
import { CompanySize } from "./components/CompanySize";
import { CompanyShortDescription } from "./components/CompanyShortDescription";
import { CompanyValues } from "./components/CompanyValues";
import { EmployeeEndorsements } from "./components/EmployeeEndorsements";
import { ExperienceBar } from "./components/ExperienceBar";
import { Insights } from "./components/Insights";
import { JobInvolves } from "./components/JobInvolves";
import { JobRequirements } from "./components/JobRequirements";
import { JobSubtitle } from "./components/JobSubtitle";
import { JobTitle } from "./components/JobTitle";
import { PeopleAtCompany } from "./components/PeopleAtCompany";
import { SalarySection } from "./components/SalarySection";
import { SalarySubmission } from "./components/SalarySubmission";
import { SectorTags } from "./components/SectorTags";
import { TechnologiesUsedTags } from "./components/TechnologiesUsedTags";
import { VisaDisclaimer } from "./components/VisaDisclaimer";
import { CompanyFunding } from "./components/CompanyFunding";
import {
  CompanyOtherJobsButton,
  CompanyOtherJobsSection,
} from "./components/CompanyOtherJobs";
import { CompanyLocation } from "./components/CompanyLocation";
import { HideCompanyLink } from "./components/HideCompanyLink";
import { JobCardSection } from "./components/JobCardSection";
import { JobLocationsSection } from "./components/JobLocationsSection";
import { OttasTake } from "./components/OttasTake";
import { ReportAProblem } from "./components/ReportAProblem";
import { ShareLink } from "./components/ShareLink";
import { SeparateContent } from "./DesktopContent";
import { StatusTags } from "./components/StatusTags";

import { Job, UserJobPreferences } from "schema";
import { DiversityInitiatives } from "components/DiversityInitiatives";

const SalaryBenchmarks = lazy(() => import("./components/SalaryBenchmarks"));

const OttaLogo = styled(Logo)`
  height: ${pxToRem(20)};
`;

const Top = styled.div<{ publicJob?: boolean }>`
  display: flex;
  height: fit-content;
  justify-content: center;
  transition: height 0.1s ease-in-out;
  width: 100%;

  ${({ publicJob }) =>
    publicJob &&
    css`
      padding-top: ${modularScale(2)};
    `}
`;

const TopCard = styled(Card)`
  border-radius: 0;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 0;
`;

const TopCardMainContent = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: ${pxToRem(24)};
  justify-content: space-between;
  max-width: 1024px;
  padding: ${pxToRem(24)};

  > .vr {
    background: ${palette.grayscale.shade500};
    width: 0.5px;
    height: 100%;
  }
`;

const TopCardSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(12)};
`;

const MainContentContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(24)};
  max-width: 1024px;
`;

const ActionButtonContainer = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: ${pxToRem(16)};
`;

const JobTitleSection = styled.div`
  gap: 0.5rem;
`;

interface DesktopContentProps {
  job: Job.Fragment;
  user?: UserJobPreferences.CurrentUser;
  otherJobsExpanded: boolean;
  expandOtherJobs?: () => void;
  publicJob: boolean;
}

export function MobileContent({
  job,
  user,
  otherJobsExpanded,
  expandOtherJobs,
  publicJob,
}: DesktopContentProps): React.ReactElement {
  return (
    <>
      <TopContent job={job} user={user} publicJob={publicJob} />
      <MainContentContainer>
        <RoleInfoSection
          job={job}
          user={user}
          otherJobsExpanded={otherJobsExpanded}
          expandOtherJobs={expandOtherJobs}
        />
        <OttasTakeSection job={job} />
        <CompanyInfoSection job={job} user={user} />
        <CompanyPhotos company={job.company} />
        <ActionButtonsSection
          job={job}
          user={user}
          otherJobsExpanded={otherJobsExpanded}
          expandOtherJobs={expandOtherJobs}
        />
      </MainContentContainer>
    </>
  );
}

function TopContent({
  job,
  user,
  publicJob,
}: Pick<DesktopContentProps, "job" | "user"> & {
  publicJob: boolean;
}): React.ReactElement {
  return (
    <Top publicJob={publicJob}>
      <TopCard>
        <TopCardMainContent>
          <SeparateContent>
            <TopCardSection>
              <JobTitleSection>
                <JobTitle job={job} company={job.company} align="left" />
                <JobSubtitle job={job} align="left" />
              </JobTitleSection>
              <CompanyLogo company={job.company} isMobile={true} />
              <CompanyShortDescription company={job.company} />
              <StatusTags company={job.company} job={job} isMobile={true} />
            </TopCardSection>

            <TopCardSection>
              <SalarySection job={job} company={job.company} />
              <TechnologiesUsedTags job={job} user={user} />
              <ExperienceBar job={job} />
              <JobLocationsSection job={job} user={user} />
            </TopCardSection>

            <TopCardSection>
              <CompanySize company={job.company} />
              <SectorTags company={job.company} user={user} />
            </TopCardSection>
            <CompanyMission company={job.company} />
          </SeparateContent>
        </TopCardMainContent>
      </TopCard>
    </Top>
  );
}

function RoleInfoSection({
  job,
  user,
}: Pick<
  DesktopContentProps,
  "job" | "user" | "expandOtherJobs" | "otherJobsExpanded"
>): React.ReactElement {
  return (
    <>
      <JobCardSection title="Role">
        <SeparateContent>
          <JobRequirements job={job} />
          <JobInvolves job={job} />
          <ApplicationProcess job={job} />
          <Suspense fallback={<Loading />}>
            <SalaryBenchmarks job={job} user={user} />
          </Suspense>
        </SeparateContent>
      </JobCardSection>
    </>
  );
}

function CompanyInfoSection({
  job,
  user,
}: Pick<DesktopContentProps, "job" | "user">): React.ReactElement {
  return (
    <>
      <JobCardSection title="Company">
        <SeparateContent>
          <Insights job={job} company={job.company} />
          <EmployeeEndorsements company={job.company} user={user} />
          <CompanyFunding company={job.company} />
          <CompanyBenefits job={job} company={job.company} />
          <CompanyValues company={job.company} />
          <CompanyLocation job={job} company={job.company} />
          <VisaDisclaimer job={job} company={job.company} user={user} />
          <CompanyArticles company={job.company} />
          <CompanyAdditionalInfo company={job.company} />
          <PeopleAtCompany company={job.company} />
          <SalarySubmission job={job} user={user} />
          <DiversityInitiatives company={job.company} />
        </SeparateContent>
      </JobCardSection>
    </>
  );
}

function OttasTakeSection({
  job,
}: Pick<DesktopContentProps, "job">): React.ReactElement {
  return (
    <JobCardSection
      title={job.company.name === "Otta" ? "Our take" : "Otta's take"}
      titleColor={palette.brand.yellow}
      tooltipDescription="This is Otta's opinion, written to help you understand more about the company. It's not intended as a statement of fact, but was written to be accurate as possible at the time of publishing."
      TopRightComponent={<OttaLogo />}
    >
      <OttasTake company={job.company} />
    </JobCardSection>
  );
}

function ActionButtonsSection({
  job,
  user,
  otherJobsExpanded,
  expandOtherJobs,
}: Pick<
  DesktopContentProps,
  "job" | "user" | "otherJobsExpanded" | "expandOtherJobs"
>): React.ReactElement {
  return (
    <>
      <JobCardSection>
        <ActionButtonContainer>
          <ShareLink job={job} />
          <ReportAProblem job={job} user={user} />
          <HideCompanyLink company={job.company} user={user} />
          <CompanyOtherJobsButton
            company={job.company}
            user={user}
            currentJobId={job.id}
            expanded={otherJobsExpanded}
            expand={expandOtherJobs}
          />
        </ActionButtonContainer>
      </JobCardSection>

      {otherJobsExpanded && job.company.liveJobs.length > 1 && (
        <motion.div
          key="job-card-company-jobs"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ opacity: { ease: "easeInOut" } }}
          style={{ width: "100%" }}
        >
          <JobCardSection>
            <CompanyOtherJobsSection
              company={job.company}
              user={user}
              currentJobId={job.id}
            />
          </JobCardSection>
        </motion.div>
      )}
    </>
  );
}
